"use client";

/**
 * @file        src/components/listing/ListingFilterFields.tsx
 * @description The shared field vocabulary of the listing filter bar: a pill row, a
 *              labelled select and a labelled from/to range. Presentational only.
 * @rule        Presentational and FORM-NATIVE. No state, no URL, no routing, no timers,
 *              no <form> — a change calls its callback and nothing else (the container
 *              is B3b). And NEVER drop a `name` attribute or turn the pills into
 *              type="button": those two details ARE the no-JS and pre-hydration path
 *              (UNIFIED-8). A buyer tapping «Китай» a second before hydration finishes
 *              must still filter the catalog. No build or lint check catches their loss.
 * @universal   knowledge/universals/components.md#ListingFilterFields
 */

import { cn } from "@/lib/utils";

// === CONSTANTS ==========================================================

/** Verbatim from the pre-2026-07 client filter (CatalogFilters.tsx, removed at E1; selectClass === inputClass there) — visual contract. */
const FIELD =
  "mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-base text-text outline-none transition-colors focus:outline-none focus:ring-2 focus:ring-primary md:text-sm";
const LABEL = "text-sm font-medium text-text";
const PILL = "rounded-full px-4 py-2 text-sm font-medium transition-colors";
const PILL_ON = "bg-primary text-white";
const PILL_OFF = "bg-surface-alt text-text-muted hover:bg-border";

// === CONTROLS ===========================================================

interface FilterTabsProps {
  name: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  allLabel: string;
  onSelect: (value: string | null) => void;
}

/**
 * The horizontal pill row (country today).
 * @input  name — the URL param key, rendered on every pill; value — the selected value
 *         or null for "all"; options; allLabel — the leading "all" pill's text;
 *         onSelect — receives the value, or null for the all pill.
 * @output A `flex flex-wrap gap-2` row of submit buttons.
 * @important Every pill is `type="submit"` with `name` + `value` (the all pill's value
 *            is `""`, which drops the param), so a click filters natively when the
 *            enclosing GET form has no JavaScript yet — the pre-hydration case that
 *            matters on a weak phone. The container may intercept the click; it must
 *            never remove the submit type. An empty `options` array renders just the
 *            all pill — the everyday case for a facet the data does not offer.
 */
export function FilterTabs({ name, value, options, allLabel, onSelect }: FilterTabsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      <button
        type="submit"
        name={name}
        value=""
        aria-pressed={value === null}
        onClick={() => onSelect(null)}
        className={cn(PILL, value === null ? PILL_ON : PILL_OFF)}
      >
        {allLabel}
      </button>
      {options.map((option) => (
        <button
          key={option.value}
          type="submit"
          name={name}
          value={option.value}
          aria-pressed={value === option.value}
          onClick={() => onSelect(option.value)}
          className={cn(PILL, value === option.value ? PILL_ON : PILL_OFF)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface FilterSelectProps {
  id: string;
  name: string;
  label: string;
  value: string | null;
  options: readonly string[] | readonly { value: string; label: string }[];
  allLabel: string;
  onSelect: (value: string | null) => void;
}

/**
 * A labelled dropdown.
 * @input  id / name — element id and URL param key; label; value — selected value or
 *         null; options — either plain strings or value/label pairs (see below);
 *         allLabel — the "no filter" entry; onSelect.
 * @output A label bound via htmlFor plus a native select carrying `name`.
 * @important Two option forms, one meaning. A plain string is the SHORTHAND for a
 *            self-labelling option — brands, body types, fuels, models, where the code
 *            and the visible text are the same word — and behaves exactly as before.
 *            A `{value,label}` pair is for a CODED option, where the two differ
 *            (`price-asc` → «Сначала дешевле»). Either way the VALUE is what lands in
 *            the URL and in a native form submit, and what `onSelect` receives; the
 *            label is display text and must never reach either (UNIFIED-8 — a label in
 *            `value` would send Russian text as the sort key on the no-JS path).
 *            The first option is `value=""` and means "no filter": choosing it passes
 *            **null**, never `""`, so the caller does not have to re-interpret an empty
 *            string. An empty `options` array renders the allLabel entry alone.
 */
export function FilterSelect({ id, name, label, value, options, allLabel, onSelect }: FilterSelectProps) {
  // One normalisation, right here: a bare string is its own value AND its own label.
  const items = options.map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );

  return (
    <div>
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        name={name}
        value={value ?? ""}
        onChange={(e) => onSelect(e.target.value === "" ? null : e.target.value)}
        className={FIELD}
      >
        <option value="">{allLabel}</option>
        {items.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

interface FilterRangeProps {
  idPrefix: string;
  nameFrom: string;
  nameTo: string;
  label: string;
  from: string;
  to: string;
  onFromChange: (raw: string) => void;
  onToChange: (raw: string) => void;
  placeholderFrom?: string;
  placeholderTo?: string;
  step?: string;
}

/**
 * A labelled from/to number pair.
 * @input  idPrefix — ids become `${idPrefix}-from` / `-to`; nameFrom / nameTo — URL
 *         param keys; label; from / to — RAW strings; the two change callbacks;
 *         optional placeholders (default "от" / "до") and step.
 * @output A label plus two native number inputs in a `mt-1 flex gap-2` row.
 * @important Values pass through untouched — this control never parses, converts or
 *            validates (litres-vs-cc and numeric sanity belong to the parser).
 */
export function FilterRange({
  idPrefix,
  nameFrom,
  nameTo,
  label,
  from,
  to,
  onFromChange,
  onToChange,
  placeholderFrom = "от",
  placeholderTo = "до",
  step,
}: FilterRangeProps) {
  return (
    <div>
      <label className={LABEL} htmlFor={`${idPrefix}-from`}>
        {label}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          id={`${idPrefix}-from`}
          name={nameFrom}
          type="number"
          inputMode="numeric"
          min="0"
          step={step}
          placeholder={placeholderFrom}
          value={from}
          onChange={(e) => onFromChange(e.target.value)}
          className={FIELD}
        />
        <input
          id={`${idPrefix}-to`}
          name={nameTo}
          type="number"
          inputMode="numeric"
          min="0"
          step={step}
          placeholder={placeholderTo}
          value={to}
          onChange={(e) => onToChange(e.target.value)}
          className={FIELD}
        />
      </div>
    </div>
  );
}
